import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FooterButtons } from '../../components/styles'
import StyledModal from '../../components/CustomModal'
import { Form, Input } from 'antd'
import { AddButton } from '../../pages/Category/styles';
import { InboxOutlined } from '@ant-design/icons';
import { UploadProps, UploadFile } from 'antd';
import { message, Upload, Button } from 'antd';
import { CustomRender, DeleteOutlinedIcon, FileLabel, FormDivider, FormItem, FormItemLabel, FormWrapperContainer, UploadedFilesLabel, UploadInfoText } from '../styles';
import ConfirmationModal from '../../components/ConfirmationModal';
import { CustomUploadFile } from '../../utils/helpers/validateUploadFiles';
import { useAppDispatch, useAppSelector } from '../../utils/hooks/redux';
import { uploadShippingProof } from '../../redux/features/report/reportAsyncThunk';
import { selectUserRole } from '../../redux/features/auth/selectors';
import { checkHasPermission } from '../../utils/helpers/checkHasPermission';
import { permissions } from '../../utils/constants/permissions';
import { toast } from 'react-toastify';
import { DateInput } from '../../components/Input';
import dayjs from "dayjs";

interface DeliveredModalProps {
    isDeliveredModalOpen: boolean;
    handleDeliveredCancel: () => void;
    handleDeliveredSuccess: () => void;
}

const DeliveredModal: React.FC<DeliveredModalProps> = ({ isDeliveredModalOpen, handleDeliveredCancel, handleDeliveredSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirmItem, setShowDeleteConfirmItem] = useState<UploadFile | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const { report } = useAppSelector((state) => state.report);
    const dispatch = useAppDispatch();

    const userRole = useAppSelector(selectUserRole);

    const hasPermission = useCallback(
        (permissionKey: string) => checkHasPermission(userRole, permissionKey),
        [userRole]
    );

    const isAddPermission = useMemo(() => hasPermission(permissions.AddEditShippingProof), [hasPermission]);
    const isEditPermission = useMemo(() => hasPermission(permissions.AddEditShippingProof), [hasPermission]);
    const isDeletePermission = useMemo(() => hasPermission(permissions.DeleteShippingProof), [hasPermission]);

    const handleFileChange = async ({ fileList: newFileList }: { fileList: CustomUploadFile[] }) => {
        // Merge existing fileList with newFileList, avoiding duplicates by UID
        const mergedFileList = [...fileList, ...newFileList].reduce((acc: UploadFile[], current) => {
            if (!acc.find(file => file.uid === current.uid)) {
                acc.push(current);
            }
            return acc;
        }, []);

        setFileList(mergedFileList);

        const blobs = mergedFileList
            .map((file: UploadFile) =>
                file.originFileObj ? new Blob([file.originFileObj], { type: file.originFileObj.type }) : null
            )
            .filter((blob) => blob !== null);

        form.setFieldsValue({ files: blobs });
    };

    const handleRemove = async (file: CustomUploadFile) => {
        const documentId = report?.shippingProof?.find((doc: any) => doc?.id?.toString() === file?.uid);
        if (documentId && file?.uid && report?.id?.length) {
            try {
                if (report?.id) {
                    setLoading(true);
                    const formData = new FormData();
                    formData.append('deletedFiles[]', file.uid);
                    const deliveryDate = await form.getFieldValue('deliveryDate');
                    const deliveryDateValue = deliveryDate ? deliveryDate?.toISOString() : "";

                    formData.append('deliveryDate', deliveryDateValue);
                    await dispatch(uploadShippingProof({ lostId: report?.id, formdata: formData, isDelete: true, deliveryDate: deliveryDateValue })).unwrap();

                    const filterFileList = fileList?.filter((f) => f?.uid !== file?.uid);
                    setFileList(filterFileList);
                    setShowDeleteConfirmItem(null);
                    message.success(`${file.name} has been deleted.`);
                }
            } catch (error) {
            } finally {
                setLoading(false);
            }
        } else {
            const filterFileList = fileList.filter((f) => f.uid !== file.uid);
            setFileList(filterFileList);
            setShowDeleteConfirmItem(null);
        }
    };


    const beforeUpload = (file: any) => {
        const isValidFileType =
            file.type === 'application/pdf' ||
            file.type === 'image/jpeg' ||
            file.type === 'image/jpg' ||
            file.type === 'image/png' ||
            file.type === 'image/webp';

        if (!isValidFileType) {
            message.error('You can only upload PDF, JPEG, JPG, PNG, or WEBP files!');
        }
        return false;
    };

    const props: UploadProps = {
        name: 'files',
        multiple: true,
        disabled: !(isAddPermission || isEditPermission),
        accept: '.pdf',
        beforeUpload,
        fileList: fileList,
        listType: "text",
        maxCount: 1,
        showUploadList: {
            showPreviewIcon: true,
            showRemoveIcon: true,
            showDownloadIcon: false,
        },
        onChange: handleFileChange,
        onDrop(e) {
            e.preventDefault();
            handleFileChange({ fileList: [...fileList] });
        },
        onRemove: handleRemove,
        itemRender: (originNode, file) => (
            <>
                {/* Show "Uploaded files" label only once, for the first file */}
                {fileList?.[0]?.uid === file.uid && (
                    <UploadedFilesLabel>Uploaded files</UploadedFilesLabel>
                )}

                <CustomRender align="center" justify='space-between'>
                    <FileLabel>{originNode}</FileLabel>
                    {isDeletePermission && <Button
                        type="text"
                        icon={<DeleteOutlinedIcon />}
                        onClick={() => setShowDeleteConfirmItem(file)}
                    />}
                </CustomRender>
            </>
        ),
    };

    const handleFormSubmit = async () => {
        try {
            if (report?.id) {
                setLoading(true);
                const values = await form.validateFields();
                let files = [];
                if (values?.files?.length > 0 && Array.isArray(values?.files)) {
                    // await form.validateFields(['files']);
                    const hasBlob = values.files.some((doc: any) => doc instanceof Blob);
                    if (hasBlob) {
                        files = values?.files;
                    }
                }
                const deliveryDate = values?.deliveryDate ? values?.deliveryDate?.toISOString() : "";
                const formattedFilesValues = {
                    ...values,
                    files: files,
                    deliveryDate,
                }
                const response = await dispatch(uploadShippingProof({ lostId: report?.id, formdata: formattedFilesValues, isDelete: false, deliveryDate })).unwrap();

                if (response) {
                    toast.success("Shipping Proof saved successfully");
                }
                form.resetFields();
                setFileList([]);
                handleDeliveredSuccess();
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (report?.id && report?.shippingProof && report?.shippingProof?.length > 0) {
            setFileList(report?.shippingProof?.map((doc: any) => ({
                uid: doc?.id?.toString(),
                name: doc?.documentName,
                status: 'done',
                url: doc?.fullUrl
            })))
        }
        if (report?.deliveryDate) {
            form.setFieldValue("deliveryDate", report?.deliveryDate ? dayjs(report?.deliveryDate) : null);
        }
    }, [report])

    return (
        <>
            <StyledModal
                draggable={true}
                width={715}
                title={report?.shippingProof && report?.shippingProof?.length > 0 ? "Uploaded Shipping Proof" : "Upload Shipping Proof"}
                centered
                open={isDeliveredModalOpen}
                onCancel={handleDeliveredCancel}
                footer={
                    <FooterButtons>
                        <AddButton shape='round' disabled={loading} onClick={handleDeliveredCancel}>Cancel</AddButton>
                        {(isAddPermission || isEditPermission) && <AddButton shape='round' loading={loading} disabled={loading} onClick={handleFormSubmit} type="primary">
                            {report?.shippingProof && report?.shippingProof?.length > 0 ? "Update" : "Save"}
                        </AddButton>}
                    </FooterButtons>
                }
                closeIcon={null}
            >
                <FormWrapperContainer disabled={loading} form={form} name="shippingProof" layout="vertical">
                    <Form.Item name="deletedFiles" hidden>
                        <Input />
                    </Form.Item>
                    <FormItem label={<FormItemLabel>Uploaded Shipping Proof</FormItemLabel>} name="files" rules={[{ required: false, message: "Please upload shipping proof." }]}>
                        <UploadInfoText></UploadInfoText>
                        <Upload.Dragger className='shipping_upload_wrapper' {...props}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Click or drag file to this area to upload shipping proof</p>
                        </Upload.Dragger>
                    </FormItem>
                    <DateInput
                        fontWeight="700"
                        label="Delivery Date"
                        placeholder="Date"
                        name="deliveryDate"
                        isDisabledFutureDate={false}
                        onChange={(v: any) => {
                            form.setFieldsValue({
                                deliveryDate: v,
                            });
                        }}
                        disabled={!isEditPermission && !isAddPermission}
                        rules={[
                            {
                                required: true,
                                message: "Please select the delivery date.",
                            },
                        ]}
                    />
                </FormWrapperContainer>
            </StyledModal>

            {showDeleteConfirmItem && (
                <ConfirmationModal
                    handleSave={() => { handleRemove(showDeleteConfirmItem); setFileList([]); }}
                    isModalOpen={!!showDeleteConfirmItem}
                    onCancel={() => setShowDeleteConfirmItem(null)}
                    title={`Confirm Delete?`}
                    message={`Are you sure you want to delete the ${showDeleteConfirmItem.name}?`}
                    confirmButtonText="Delete"
                    loading={loading}
                />
            )}

        </>
    )
}

export default DeliveredModal;
